import React, { useState } from 'react';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { Helmet } from 'react-helmet-async';
import ComplianceAIForm from '@/components/compliance-ai/ComplianceAIForm';
import ComplianceAIResponse from '@/components/compliance-ai/ComplianceAIResponse';
import { ComplianceFormData } from '@/types/compliance';
import { mockComplianceAiResponse } from '@/mocks/complianceAiResponse';
import { API_CONFIG } from '@/constants/env.ts';
import { Hero } from '@/components/ui/hero-with-image-text-and-two-buttons';
import LoadingAnimation from '@/components/ui/loading-animation';

const CTAFormPage = () => {
  const [formData, setFormData] = useState<ComplianceFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<any | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(90); // 90 second countdown

  const handleFormSubmit = async (data: ComplianceFormData) => {
    setFormData(data);
    setIsLoading(true);
    setError(null);
    setTimeRemaining(90); // Reset timer to 90 seconds

    try {
      // Use the actual API endpoint from API_CONFIG
      const apiUrl = `${API_CONFIG.API_BASE_URL}/compliance-ai/`;
      console.log('Sending request to compliance AI API:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Origin: window.location.origin,
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(
          `API request failed with status ${response.status}: ${response.statusText}`
        );
      }

      let responseData = await response.json();

      console.log('API Response:', responseData);

      // Handle different response formats
      if (typeof responseData === 'string') {
        try {
          // Try to parse if it's a JSON string
          responseData = JSON.parse(responseData);
        } catch (e) {
          // If it's not valid JSON, wrap it in a content property
          responseData = { content: responseData };
        }
      }

      // Handle direct response object format (post-processed by backend)
      if (
        responseData.text !== undefined &&
        responseData.advice !== undefined &&
        responseData.diagram !== undefined
      ) {
        console.log('Got well-formed response from API with text/advice/diagram properties');

        // Fix potential diagram issues if it's a string (for backward compatibility)
        if (typeof responseData.diagram === 'string') {
          fixDiagramSyntax(responseData);
        }
      }
      // Handle nested content property
      else if (responseData.content && typeof responseData.content === 'string') {
        try {
          const parsed = JSON.parse(responseData.content);
          if (parsed.text || parsed.advice || parsed.diagram) {
            responseData = parsed;
            if (typeof responseData.diagram === 'string') {
              fixDiagramSyntax(responseData);
            }
          }
        } catch (e) {
          console.error('Failed to parse content string:', e);
        }
      }
      // Handle Python dict-like representation
      else if (
        typeof responseData === 'object' &&
        Object.keys(responseData).some(key => key.includes("'"))
      ) {
        console.log('Got Python dict-like response, converting to proper JSON');
        try {
          // Convert Python dict format to proper JSON object
          const fixedData = {};

          Object.keys(responseData).forEach(key => {
            // Remove single quotes and fix key names
            const cleanKey = key.replace(/'/g, '');
            fixedData[cleanKey] = responseData[key];
          });

          // Set the properly formatted data
          responseData = fixedData;

          // Fix potential diagram issues if it's a string
          if (typeof responseData.diagram === 'string') {
            fixDiagramSyntax(responseData);
          }

          console.log('Converted response:', responseData);
        } catch (e) {
          console.error('Failed to convert Python dict format:', e);
        }
      }

      console.log('Final processed response for component:', responseData);

      // Ensure we show the loading animation for at least 5 seconds
      const elapsedTime = 90 - timeRemaining;
      if (elapsedTime < 5) {
        // Wait until we've shown the animation for at least 5 seconds
        setTimeout(
          () => {
            setResponse(responseData);
            setIsLoading(false);
          },
          (5 - elapsedTime) * 1000
        );
      } else {
        setResponse(responseData);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);

      if (error instanceof Error) {
        console.log('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
        setError(error);
      } else {
        setError(new Error('An unknown error occurred'));
      }

      // Fallback to mock data if API fails
      if (API_CONFIG.USE_MOCK_API) {
        console.log('Falling back to mock data due to API error');
        setTimeout(() => {
          setResponse({
            ...mockComplianceAiResponse,
            title: 'Financial Marketing Compliance Assessment',
            summary:
              'Your financial marketing materials have been analyzed for regulatory compliance. Here are our recommendations to improve your compliance posture.',
          });
        }, 1000);
      }
    }
  };

  // Helper function to fix diagram syntax issues
  function fixDiagramSyntax(dataObj) {
    if (!dataObj.diagram || typeof dataObj.diagram !== 'string') return;

    // Special case for the exact diagram format in the console log
    if (dataObj.diagram.includes('style F1 fill:#90EE90') && !dataObj.diagram.endsWith('end')) {
      console.log('Detected the exact diagram format from the console log');
      dataObj.diagram += '\n    end';
      console.log('Fixed the diagram by adding missing end tag');
      return;
    }

    // Count subgraph and end tags
    const subgraphCount = (dataObj.diagram.match(/subgraph/g) || []).length;
    const endCount = (dataObj.diagram.match(/end/g) || []).length;

    console.log(`Diagram analysis - Subgraph count: ${subgraphCount}, End count: ${endCount}`);

    // Add missing end tags
    if (subgraphCount > endCount) {
      for (let i = 0; i < subgraphCount - endCount; i++) {
        dataObj.diagram += ' end';
      }
    }

    // For diagrams with style declarations at the end
    if (dataObj.diagram.match(/style\s+[A-Z0-9]+\s+fill:#[0-9A-F]{6}$/i)) {
      console.log('Diagram ends with a style declaration, adding end tag');
      dataObj.diagram += '\n    end';
    }

    // Log the fixed diagram
    console.log('Fixed diagram:', dataObj.diagram);
  }

  // Form component to be rendered in the right column of the Hero component
  const FormComponent = () => (
    <ComplianceAIForm onSubmit={handleFormSubmit} isLoading={isLoading} error={error} />
  );

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <Helmet>
        <title>AI-Powered Financial Marketing Compliance | Verdea AI</title>
        <meta
          name="description"
          content="Transform your financial marketing compliance with AI. Streamline pre-approval reviews and ensure regulatory compliance across all marketing channels."
        />
      </Helmet>

      {/* Navbar - only shown when not loading */}
      {!isLoading && (
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
          <NavBar landingMode={true} />
        </header>
      )}

      <main className="relative z-10">
        {/* Main content with hero and form */}
        {!response && !isLoading ? (
          <Hero rightContent={<FormComponent />} />
        ) : isLoading ? (
          <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <div className="bg-card p-8 rounded-2xl shadow-xl border border-border">
                  <LoadingAnimation
                    timeRemaining={timeRemaining}
                    setTimeRemaining={setTimeRemaining}
                    initialTime={90}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <section className="py-12 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <div className="bg-card p-8 rounded-2xl shadow-xl border border-border">
                  <ComplianceAIResponse
                    response={response}
                    isLoading={isLoading}
                    error={error}
                    onReset={() => {
                      setFormData(null);
                      setResponse(null);
                      setError(null);
                      setTimeRemaining(90);
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

      </main>

      {!isLoading && (
        <footer>
          <Footer landingMode={true} />
        </footer>
      )}
    </div>
  );
};

export default CTAFormPage;
